import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Typography, Drawer } from "antd";
import { message } from "antd";
import { formatCurrency } from "../../servos/numberHelpers";
import {
	makePaymentRequest,
	makePaymentFailure,
	startBookingProcess,
} from "./actions";
import { getPaymentIntent } from "./api";
import {
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import "./CardSectionStyles.css";
import { isMobile } from "react-device-detect";
import { closePaymentDrawer } from "./actions";
import { LockOutlined, CreditCardOutlined } from "@ant-design/icons";
import { trackEvent, trackFBStandardEvent, trackButtonClick, trackFormError } from "utils/analytics";

const CARD_ELEMENT_OPTIONS = {
	style: {
		base: {
			color: "#32325d",
			fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
			fontSmoothing: "antialiased",
			fontSize: "16px",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#fa755a",
			iconColor: "#fa755a",
		},
	},
};

function CardForm({ practiceId, serviceTotal, surcharge, totalWithSurcharge }) {
	const dispatch = useDispatch();
	const stripe = useStripe();
	const elements = useElements();

	const patient = useSelector((state) => state.booking.currentPatient);
	const submitting = useSelector((state) => state.booking.saveBookingState);
	const bookingDate = useSelector((state) => state.booking.fromDate);
	const patientNote = useSelector((state) => state.booking.patientNote);
	const currentPractice = useSelector(
		(state) => state.locations.currentLocation,
	);
	const availabilityRequests = useSelector(
		(state) => state.booking.availabilityRequests,
	);

	const applySurcharge = React.useMemo(
		() => currentPractice?.applyPatientSurcharge,
		[currentPractice],
	);

	const themeColor = currentPractice?.themeColor;

	const visible = useSelector((state) => state.booking.paymentDrawerState);

	const handleSubmit = async (event) => {
		event.preventDefault();

		trackEvent("payment_attempted", { amount: totalWithSurcharge });
		trackButtonClick('payment_submit_button', { amount: totalWithSurcharge });
		dispatch(startBookingProcess());

		if (!stripe || !elements) {
			return;
		}

		const {
			response,
		} = await getPaymentIntent({
			practiceId: practiceId,
			patientId: patient.id ? patient.id : patient,
		});

		if (response) {
			const result = await stripe.confirmCardSetup(response.clientSecret, {
				payment_method: {
					card: elements.getElement(CardNumberElement),
					billing_details: {
						name: document.getElementById("card-name").value,
					},
				},
			});

			if (result.error) {
				dispatch(makePaymentFailure());
				trackEvent("payment_failed", { error: result.error.message });
				trackFormError('payment_form', 'stripe_error', result.error.message);

				message.error(
					`Sorry, we couldn't process your payment. ${result?.error?.message}`,
				);
			} else {
				trackEvent("payment_succeeded", { amount: totalWithSurcharge });
				trackEvent("booking_confirmed", {
					total_amount: totalWithSurcharge,
					has_payment: true,
				});
				trackFBStandardEvent("Schedule");
				trackFBStandardEvent("Purchase", {
					value: totalWithSurcharge,
					currency: "AUD",
				});
				dispatch(
					makePaymentRequest({
						practiceId,
						patientId: patient.id ? patient.id : patient,
						card: result,
						availabilityRequests,
						bookingDate,
						patientNote,
						successUrl: currentPractice?.successUrl,
					}),
				);
				dispatch(closePaymentDrawer());
			}
		} else {
			dispatch(makePaymentFailure());
			trackFormError('payment_form', 'api_error', 'payment_intent_failed');
			message.error("Sorry, something went wrong. Please try again later.");
		}
	};

	return (
		<Drawer
			title={null}
			placement="right"
			width={isMobile ? "100vw" : 440}
			open={visible}
			closable={false}
			onClose={() => {
				dispatch(closePaymentDrawer());
			}}
			styles={{
				body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
				header: { display: 'none' },
			}}
		>
			{/* Header */}
			<div style={{
				padding: '16px 24px',
				borderBottom: '1px solid #f0f0f0',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
			}}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<CreditCardOutlined style={{ fontSize: 18, color: themeColor || '#333' }} />
					<Typography.Text strong style={{ fontSize: 16 }}>
						Payment
					</Typography.Text>
				</div>
				<Button
					type="text"
					onClick={() => dispatch(closePaymentDrawer())}
					style={{ fontSize: 18, color: '#999', padding: '4px 8px' }}
				>
					&times;
				</Button>
			</div>

			{/* Content */}
			<div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
				{/* Price Summary */}
				<div style={{
					background: '#f8f8f8',
					borderRadius: 12,
					padding: '16px 20px',
					marginBottom: 24,
				}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: applySurcharge ? 8 : 0,
					}}>
						<Typography.Text type="secondary" style={{ fontSize: 14 }}>
							Service Total
						</Typography.Text>
						<Typography.Text style={{ fontSize: 14 }}>
							{formatCurrency(serviceTotal)}
						</Typography.Text>
					</div>
					{applySurcharge && (
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: 8,
						}}>
							<Typography.Text type="secondary" style={{ fontSize: 14 }}>
								Handling Fee
							</Typography.Text>
							<Typography.Text style={{ fontSize: 14 }}>
								{formatCurrency(surcharge)}
							</Typography.Text>
						</div>
					)}
					{applySurcharge && (
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							paddingTop: 12,
							borderTop: '1px solid #e8e8e8',
						}}>
							<Typography.Text strong style={{ fontSize: 16 }}>
								Total
							</Typography.Text>
							<Typography.Text strong style={{ fontSize: 18 }}>
								{formatCurrency(totalWithSurcharge)}
							</Typography.Text>
						</div>
					)}
				</div>

				{/* Card Form */}
				<Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 16 }}>
					Card Details
				</Typography.Text>

				<form>
					<div className="card-elements">
						<Row>
							<Col span={24}>
								<label className="card__input-wrapper card__input-wrapper--block">
									<Typography.Text type="secondary" style={{ fontSize: 13, marginBottom: 6, display: 'block' }}>
										Name on Card
									</Typography.Text>
									<div className="card-name field">
										<input
											id="card-name"
											name="name"
											placeholder="Jenny Rosen"
											className="form-control"
											required
										/>
									</div>
								</label>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<label className="card__input-wrapper card__input-wrapper--block">
									<Typography.Text type="secondary" style={{ fontSize: 13, marginBottom: 6, display: 'block' }}>
										Card Number
									</Typography.Text>
									<div className="card-number field">
										<CardNumberElement options={CARD_ELEMENT_OPTIONS} />
									</div>
								</label>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={12}>
								<label className="card__input-wrapper card__input-wrapper--block">
									<Typography.Text type="secondary" style={{ fontSize: 13, marginBottom: 6, display: 'block' }}>
										Expiry
									</Typography.Text>
									<div className="card-expiry field">
										<CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
									</div>
								</label>
							</Col>
							<Col span={12}>
								<label className="card__input-wrapper card__input-wrapper--block">
									<Typography.Text type="secondary" style={{ fontSize: 13, marginBottom: 6, display: 'block' }}>
										CVC
									</Typography.Text>
									<div className="card-cvc field">
										<CardCvcElement options={CARD_ELEMENT_OPTIONS} />
									</div>
								</label>
							</Col>
						</Row>
					</div>
				</form>

				{/* Security note */}
				<div style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					marginTop: 16,
				}}>
					<LockOutlined style={{ color: '#999', fontSize: 12 }} />
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						Payments are securely processed by Stripe
					</Typography.Text>
				</div>
			</div>

			{/* Footer */}
			<div style={{
				padding: '16px 24px',
				borderTop: '1px solid #f0f0f0',
				background: '#fff',
			}}>
				<Button
					type="primary"
					size="large"
					block
					onClick={handleSubmit}
					disabled={!stripe || submitting}
					loading={submitting}
					style={{
						height: 48,
						fontWeight: 500,
						borderRadius: 8,
						...(themeColor ? {
							backgroundColor: themeColor,
							borderColor: themeColor,
						} : {}),
					}}
				>
					Pay {formatCurrency(totalWithSurcharge)} and Confirm
				</Button>
			</div>
		</Drawer>
	);
}

export default CardForm;
